import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Loader2 } from 'lucide-react';

interface FeatureFlagToggleProps {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => Promise<void>;
  disabled?: boolean;
}

export function FeatureFlagToggle({ title, description, enabled, onToggle, disabled = false }: FeatureFlagToggleProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState(enabled);

  const handleToggle = async (checked: boolean) => {
    if (loading || disabled) return;

    setLoading(true);
    try {
      await onToggle(checked);
      setCurrentValue(checked);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Switch
              id={`flag-${title}`}
              checked={currentValue}
              onCheckedChange={handleToggle}
              disabled={disabled}
            />
          )}
          <Label htmlFor={`flag-${title}`} className="cursor-pointer">
            {currentValue ? t('featureFlags.enabled') : t('featureFlags.disabled')}
          </Label>
        </div>
      </CardContent>
    </Card>
  );
}
